import React, { useState, useCallback } from "react";
import { auth, db } from "../firebase";
import Swal from "sweetalert2";
import { useDropzone } from "react-dropzone";
import { processFile, processFilesAsGroup } from "../utils/extraction";

interface FileItem {
  file: File;
  selected: boolean;
  groupId: string | null;
  multiPageAsSingle: boolean;
}

interface InvoiceUploadProps {
  type: "purchase" | "sales" | "expense";
  onUploadSuccess: () => void;
}

const GROUP_COLORS = [
  "border-blue-400 bg-blue-50",
  "border-green-400 bg-green-50",
  "border-purple-400 bg-purple-50",
  "border-amber-400 bg-amber-50",
  "border-rose-400 bg-rose-50",
];

const InvoiceUpload: React.FC<InvoiceUploadProps> = ({
  type,
  onUploadSuccess,
}) => {
  const [fileItems, setFileItems] = useState<FileItem[]>([]);
  const [processing, setProcessing] = useState<boolean>(false);
  const [groupCounter, setGroupCounter] = useState<number>(1);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFileItems((prev) => [
      ...prev,
      ...acceptedFiles.map((file) => ({
        file,
        selected: false,
        groupId: null,
        multiPageAsSingle: false,
      })),
    ]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const toggleSelect = (index: number) => {
    setFileItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, selected: !item.selected } : item,
      ),
    );
  };

  const toggleMultiPage = (index: number) => {
    setFileItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, multiPageAsSingle: !item.multiPageAsSingle }
          : item,
      ),
    );
  };

  const groupSelected = () => {
    const newGroupId = `Group ${groupCounter}`;
    setGroupCounter((prev) => prev + 1);
    setFileItems((prev) =>
      prev.map((item) =>
        item.selected
          ? { ...item, selected: false, groupId: newGroupId }
          : item,
      ),
    );
  };

  const ungroup = (groupId: string) => {
    setFileItems((prev) =>
      prev.map((item) =>
        item.groupId === groupId ? { ...item, groupId: null } : item,
      ),
    );
  };

  const removeFile = (index: number) => {
    setFileItems((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (fileItems.length === 0) return;

    const user = auth.currentUser;
    if (!user) {
      Swal.fire("Error", "No user logged in", "error");
      return;
    }

    setProcessing(true);

    try {
      const groups = new Map<string | null, FileItem[]>();
      for (const item of fileItems) {
        const key = item.groupId;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(item);
      }

      for (const [groupId, items] of groups) {
        if (groupId === null) {
          for (const item of items) {
            await processFile(
              item.file,
              user,
              db,
              type,
              false,
              "",
              item.multiPageAsSingle,
            );
          }
        } else {
          await processFilesAsGroup(
            items.map((i) => i.file),
            user,
            db,
            type,
          );
        }
      }

      setFileItems([]);
      Swal.fire("Success", "Invoices uploaded successfully", "success");
      onUploadSuccess();
    } catch (error) {
      console.error("Error uploading files:", error);
      Swal.fire("Error", "Error uploading files", "error");
    } finally {
      setProcessing(false);
    }
  };

  const uniqueGroups = [
    ...new Set(fileItems.map((f) => f.groupId).filter(Boolean)),
  ] as string[];
  const selectedCount = fileItems.filter((f) => f.selected).length;
  const pdfItems = fileItems.filter((f) => f.file.type === "application/pdf");
  const allPdfsSingle =
    pdfItems.length > 0 && pdfItems.every((f) => f.multiPageAsSingle);
  const somePdfsSingle = pdfItems.some((f) => f.multiPageAsSingle);

  const setAllMultiPage = (value: boolean) => {
    setFileItems((prev) =>
      prev.map((item) =>
        item.file.type === "application/pdf"
          ? { ...item, multiPageAsSingle: value }
          : item,
      ),
    );
  };

  const getGroupColorClass = (groupId: string) => {
    const idx = uniqueGroups.indexOf(groupId);
    return GROUP_COLORS[idx % GROUP_COLORS.length];
  };

  return (
    <div className="p-4 flex flex-col">
      <h2 className="text-2xl mb-4">
        {type === "purchase"
          ? "Upload Purchase Invoice"
          : type === "sales"
            ? "Upload Sales Invoice"
            : "Upload Expense Invoice"}
      </h2>

      <div
        {...getRootProps()}
        className="border-dashed border-2 border-gray-400 p-4 mb-3 cursor-pointer hover:border-blue-400 transition-colors"
      >
        <input {...getInputProps()} />
        <p className="text-center text-gray-500 text-sm">
          Drag 'n' drop files here, or click to select
        </p>
      </div>

      {fileItems.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-gray-500">
              {selectedCount} selected
            </span>
            <button
              onClick={groupSelected}
              disabled={selectedCount < 2}
              className={`text-sm px-3 py-1 rounded border transition-colors ${
                selectedCount >= 2
                  ? "bg-indigo-500 text-white border-indigo-500 hover:bg-indigo-600"
                  : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
              }`}
            >
              Group as one invoice
            </button>
            {uniqueGroups.length > 0 && (
              <span className="text-xs text-gray-400 ml-1">
                {uniqueGroups.length} group{uniqueGroups.length > 1 ? "s" : ""}
              </span>
            )}
          </div>

          {pdfItems.length > 0 && (
            <label
              className="flex items-center gap-2 mb-2 cursor-pointer text-sm text-gray-700"
              title="Toggle single invoice mode for all PDFs"
            >
              <input
                type="checkbox"
                checked={allPdfsSingle}
                ref={(el) => {
                  if (el) el.indeterminate = somePdfsSingle && !allPdfsSingle;
                }}
                onChange={(e) => setAllMultiPage(e.target.checked)}
                className="cursor-pointer accent-indigo-500"
              />
              Treat all PDF pages as single invoices
            </label>
          )}

          <div className="overflow-y-auto max-h-64 mb-3 border rounded divide-y">
            {fileItems.map((item, index) => (
              <div
                key={index}
                className={`flex items-center gap-2 px-3 py-2 ${
                  item.groupId
                    ? `border-l-4 ${getGroupColorClass(item.groupId)}`
                    : "bg-white"
                }`}
              >
                <input
                  type="checkbox"
                  checked={item.selected}
                  onChange={() => toggleSelect(index)}
                  className="cursor-pointer accent-indigo-500"
                />
                <span
                  className="flex-1 text-sm truncate"
                  title={item.file.name}
                >
                  {item.file.name}
                </span>
                {item.file.type === "application/pdf" && (
                  <label
                    className="flex items-center gap-1 cursor-pointer text-xs text-gray-600 whitespace-nowrap"
                    title="Treat all pages in this PDF as one invoice"
                  >
                    <input
                      type="checkbox"
                      checked={item.multiPageAsSingle}
                      onChange={() => toggleMultiPage(index)}
                      className="cursor-pointer accent-indigo-500"
                    />
                    Single invoice
                  </label>
                )}
                {item.groupId && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white border border-gray-300 text-gray-600 whitespace-nowrap">
                    {item.groupId}
                  </span>
                )}
                <button
                  onClick={() => removeFile(index)}
                  className="text-gray-400 hover:text-red-500 text-sm leading-none ml-1"
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          {uniqueGroups.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {uniqueGroups.map((g) => (
                <span
                  key={g}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs border-l-4 ${getGroupColorClass(g)}`}
                >
                  {g}
                  <button
                    onClick={() => ungroup(g)}
                    className="text-gray-400 hover:text-red-500 ml-1"
                    title="Ungroup"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}
        </>
      )}

      <button
        onClick={uploadFiles}
        disabled={processing || fileItems.length === 0}
        className="bg-blue-500 text-white p-2 w-full disabled:opacity-50 hover:bg-blue-600 transition-colors"
      >
        {processing ? "Processing..." : "Upload"}
      </button>
    </div>
  );
};

export default InvoiceUpload;
